// The shortlist's async ATS export dialogue — `extension-alert-candidates-export-modal.tpl.html`
// and its contacts copy `extension-alert-client-contacts-export-modal.tpl.html` (identical but for
// indentation), shown by `onCandidatesExportRequest` / `onClientContactsExportRequest` with
// `useUnifiedTemplate: true, windowClass: "dialog-unified shortlist-dialogue export-candidates-modal"`.
//
// It is reached two ways and both end here: the PLAIN bulk Export (`onExportCandidates`, a new
// private list named after the hunter — omit `target`), and the existing-list panel's submit
// (`OnCandidatesExport` / `OnClientContactsExport`, which carries its own `target`).
//
// ⚠️ THE ✕ HIDES THE DIALOGUE, IT DOES NOT END THE EXPORT. `modal.closing` prevents only backdrop
// click and Escape; the header's ✕ (`dismiss('close')`) closes the window and leaves the interval
// running, so a success that arrives afterwards still records the export and flags the rows. That
// is why this component reports `onClose` only once it has NOTHING left to do — dismissed, poll
// stopped, record written — and why the route must keep it mounted until then. Unmounting it on
// the ✕ would silently drop the export's second write (`exportCandidates`).
//
// Sources: `extension-alert-candidates.component.ng1.ts:990-1186,1652`,
// `extension-alert-client-contacts.component.ng1.ts:909-1040,1480`.
import { useEffect, useRef, useState, type ReactElement } from 'react'
import {
  exportCandidates,
  exportClientContacts,
  exportClientContactsToAtsList,
  exportStatus,
  exportToAtsList,
  type ExportListTarget,
} from '@woo-ext/api'
import { UnifiedModal } from '../modal/UnifiedModal.tsx'
import type { ExportEntity } from './CandidateExportPanel.tsx'
import {
  EXPORT_POLL_INTERVAL_MS,
  INITIAL_EXPORT_TO_ATS_STATE,
  exportGoToAtsCopy,
  exportModalMayClose,
  exportToAtsView,
  onExportStartFailed,
  onExportStatus,
  onNotifyMe,
  opensHotlistInAts,
  plainExportTarget,
  type ExportPollStep,
  type ExportToAtsState,
  type ExportToAtsView,
} from './exportToAts.ts'

export interface ExportToAtsModalProps {
  entity: ExportEntity
  alertId: number
  /** `extensionAlert.name` — the new list's name on the plain Export. Ignored when `target` is given. */
  alertName: string
  /** The checked rows' ids, read once when the dialogue opens. */
  ids: readonly number[]
  /**
   * The existing-list panel's choice (`ExportSubmitValues` → `ExportListTarget`). Absent, this is
   * the plain Export: `{ name: alertName, description: '', isPublic: false }`.
   */
  target?: ExportListTarget
  /** `careero.atsName` — the Done button's label, and whether it opens the hotlist. */
  atsName?: string
  /**
   * The export is recorded in Woo (`exportCandidates` / `exportClientContacts` answered). The route
   * owes upstream's `exportCandidatesDecisionData` tail from here: flag the rows, drop them from
   * the list, reset the selection — see the report that came with this component.
   */
  onExported: (ids: number[]) => void
  /**
   * `openHotlistInAts(currentExportResponse.outputParamsJson)` — Bullhorn only. Upstream posts
   * `{ type: IFrameEventType.OpenHotlist, data: { outputParams } }` to the parent frame.
   */
  onOpenInAts: (outputParamsJson: unknown) => void
  /** Nothing left to do: dismissed, the poll stopped, the record written. Unmount now. */
  onClose: () => void
}

export function ExportToAtsModal({
  entity,
  alertId,
  alertName,
  ids,
  target,
  atsName,
  onExported,
  onOpenInAts,
  onClose,
}: ExportToAtsModalProps): ReactElement {
  // The request is fixed when the dialogue opens. ⚠️ Upstream re-reads `checkedCandidateIds` when
  // the export COMPLETES, so a selection changed during the poll was what got recorded; the ids
  // that were actually exported are what this records.
  const request = useRef({
    entity,
    alertId,
    ids: [...ids],
    target: target ?? plainExportTarget(alertName),
  })
  const callbacks = useRef({ onExported, onOpenInAts, onClose })
  useEffect(() => {
    callbacks.current = { onExported, onOpenInAts, onClose }
  }, [onExported, onOpenInAts, onClose])

  const [state, setState] = useState<ExportToAtsState>(INITIAL_EXPORT_TO_ATS_STATE)
  // The interval's callback reads this, not `state`: a closure would hold the first render's.
  const stateRef = useRef(state)
  const [exportMessageId, setExportMessageId] = useState<number | undefined>(undefined)
  const startedAt = useRef(0)
  const [dismissed, setDismissed] = useState(false)
  const [recording, setRecording] = useState(false)
  const [goToAtsCopy] = useState(() => exportGoToAtsCopy(atsName))

  const setExportState = (next: ExportToAtsState): void => {
    stateRef.current = next
    setState(next)
  }

  /** `exportCandidatesDecisionData` — the second write, then the route's row bookkeeping. */
  const complete = (): void => {
    const { entity: kind, alertId: id, ids: exported } = request.current
    if (exported.length === 0) return
    setRecording(true)
    const record = kind === 'candidates' ? exportCandidates(id, exported) : exportClientContacts(id, exported)
    record
      .then(() => {
        callbacks.current.onExported([...exported])
      })
      // Unhandled upstream: the flags are not set and the generic request-failure alert reports it.
      .catch(() => undefined)
      .finally(() => {
        setRecording(false)
      })
  }

  const apply = (step: ExportPollStep): void => {
    setExportState(step.state)
    if (step.complete) complete()
  }

  // ⚠️ ONE START, EVER. This writes into the customer's ATS, and the SPA renders under
  // `StrictMode`, which runs every mount effect twice in development — an unguarded effect creates
  // two lists. The ref survives StrictMode's simulated remount; the effect's second run is a no-op.
  const started = useRef(false)
  useEffect(() => {
    if (started.current) return
    started.current = true
    const { entity: kind, ids: exported, target: list } = request.current
    const start =
      kind === 'candidates'
        ? exportToAtsList({ candidateIds: exported, ...list })
        : exportClientContactsToAtsList({ clientContactIds: exported, ...list })
    start
      .then((response) => {
        startedAt.current = Date.now()
        setExportMessageId(response.exportMessageId)
      })
      .catch(() => {
        setExportState(onExportStartFailed(stateRef.current))
      })
  }, [])

  // `$interval(() => exportCandidatesPollStatus(...), 1000)`. One request at a time: upstream's
  // interval fires regardless, so a slow status call overlaps the next; skipping the tick instead
  // keeps a success from completing twice.
  useEffect(() => {
    if (exportMessageId === undefined) return undefined
    let inFlight = false
    const timer = setInterval(() => {
      if (!stateRef.current.polling) {
        clearInterval(timer)
        return
      }
      if (inFlight) return
      inFlight = true
      exportStatus(exportMessageId)
        .then((response) => {
          apply(onExportStatus(stateRef.current, response, Date.now() - startedAt.current))
        })
        // A failed poll leaves `currentExportResponse` as it was and the next tick retries, as
        // upstream's does; the request layer has already raised its alert.
        .catch(() => undefined)
        .finally(() => {
          inFlight = false
        })
    }, EXPORT_POLL_INTERVAL_MS)
    return () => {
      clearInterval(timer)
    }
    // `apply` is recreated each render but reads only refs and setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportMessageId])

  useEffect(() => {
    if (dismissed && !state.polling && !recording) callbacks.current.onClose()
  }, [dismissed, state.polling, recording])

  /** `notifyMe()`: `ok()`, then cancel the poll and complete without waiting for success. */
  const notifyMe = (): void => {
    setDismissed(true)
    apply(onNotifyMe(stateRef.current))
  }

  /** `openInBullhorn()`: `ok()`, then the hotlist on Bullhorn only. */
  const openInBullhorn = (): void => {
    setDismissed(true)
    if (opensHotlistInAts(atsName)) callbacks.current.onOpenInAts(stateRef.current.response?.outputParamsJson)
  }

  return (
    <ExportToAtsDialogue
      open={!dismissed}
      view={exportToAtsView(state)}
      goToAtsCopy={goToAtsCopy}
      onNotifyMe={notifyMe}
      onGoToAts={openInBullhorn}
      onDismiss={() => {
        setDismissed(true)
      }}
    />
  )
}

export interface ExportToAtsDialogueProps {
  /** False once dismissed. The owner may stay mounted after that — see `ExportToAtsModal`. */
  open: boolean
  view: ExportToAtsView
  /** `goToATSCopy` — "View in Bullhorn", "Close" or "Go to list". */
  goToAtsCopy: string
  /** `notifyMe()`. */
  onNotifyMe: () => void
  /** `openInBullhorn()` — the "Done!" button, whatever its label says. */
  onGoToAts: () => void
  /** The header's ✕ (`dismiss('close')`). Backdrop and Escape are prevented. */
  onDismiss: () => void
}

/**
 * The dialogue itself — both export modals' template, shared by the lists' `ExportToAtsModal` and
 * the single-profile `SingleProfileExportModal` (cee70a51e8), which shows the SAME two templates
 * with the same window class. Only what the buttons DO differs, so that is all the owners supply.
 */
export function ExportToAtsDialogue({
  open,
  view,
  goToAtsCopy,
  onNotifyMe,
  onGoToAts,
  onDismiss,
}: ExportToAtsDialogueProps): ReactElement {
  return (
    <UnifiedModal
      open={open}
      windowClass="dialog-unified shortlist-dialogue export-candidates-modal"
      onClose={onDismiss}
      onClosing={exportModalMayClose}
    >
      <div className="extension-export-candidates-overlay">
        {view === 'inProgress' ? (
          <div>
            <div className="extension-export-candidates-spinner">
              <i className="fa fa-2x fa-spinner fa-spin" />
            </div>
            <div className="extension-export-candidates-text candidates-text-center mT15">Export in progress...</div>
          </div>
        ) : (
          <div>
            {view === 'slow' ? (
              <div>
                <div className="extension-export-candidates-text export-response-delay">
                  This is taking longer than expected.
                  <br />
                  <br />
                  Click here if you wish to be notified once the export is completed.
                </div>
                <div className="export-candidates-button mT15">
                  <button type="button" className="ok-button" onClick={onNotifyMe}>
                    Notify me
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="extension-export-candidates-text candidates-text-center">Done!</div>
                <div className="export-candidates-button mT15">
                  <button type="button" className="go-to-bullhorn-button" onClick={onGoToAts}>
                    {goToAtsCopy}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </UnifiedModal>
  )
}
